import { useState, useMemo } from 'react';

import { SelectedVariantSummary } from './SelectedVariantSummary';
import { ProductVariantListItem } from './ProductVariantListItem';
import { ProductsV2Autocomplete } from './ProductsV2Autocomplete';
import { Label } from '@radix-ui/react-label';
import { Grid } from '@/components/common/grid';
import { ProductAttributeSelection } from './ProductAttributeSelection';
import { Button } from '../ui/button';

type ProductVariantMatch = {
  productVariantId: string;
  confidence: number;
};

export function AssociateProductV2({
  allowProductSelection,
  selectedProductVariantId,
  selectedProductId,
  topProductVariantMatches,
  onVariantSelected,
  onProductSelected,
  children,
  customerId,
}: {
  allowProductSelection?: boolean;
  selectedProductVariantId?: string;
  selectedProductId?: string;
  topProductVariantMatches?: ProductVariantMatch[];
  editable?: boolean;
  onVariantSelected: ({
    productId,
    productVariantId,
  }: {
    productId?: string;
    productVariantId?: string;
  }) => void;
  onProductSelected?: (productId: string | undefined) => void;
  children?: React.ReactNode;
  customerId?: string;
}) {
  const [selectedProductIdForVariant, setSelectedProductIdForVariant] =
    useState<string | undefined>(selectedProductId);

  // Fetch top product variant matches data
  const topVariantIds = useMemo(() => {
    if (!topProductVariantMatches) return [];
    return topProductVariantMatches
      .sort((a, b) => b.confidence - a.confidence)
      .slice(0, 5) // Show top 5 matches
      .map((match) => match.productVariantId);
  }, [topProductVariantMatches]);

  const handleVariantSelect = (productVariantId: string) => {
    onVariantSelected({
      productVariantId,
      productId: selectedProductIdForVariant,
    });
  };

  // Display top matches section
  const renderTopMatches = () => {
    if (topVariantIds.length === 0) return null;

    return (
      <div>
        <h2 className="text-base font-medium">Top Matches</h2>
        <ul className="list-none space-y-1">
          {topVariantIds.map((variantId) => {
            return (
              <ProductVariantListItem
                key={variantId}
                variantId={variantId}
                isSelected={selectedProductVariantId === variantId}
                onSelect={handleVariantSelect}
              />
            );
          })}
        </ul>
      </div>
    );
  };

  return (
    <Grid container spacing={2}>
      {children}

      {/* Always show the selected/suggested variant summary */}
      {selectedProductVariantId && (
        <Grid item xs={12}>
          <SelectedVariantSummary variantId={selectedProductVariantId} />
        </Grid>
      )}

      {/* Top Product Variant Matches */}
      {renderTopMatches()}

      {/* Product Search */}
      <Grid item xs={12} className="flex gap-2">
        <Label className="flex flex-col gap-2 grow">
          {topVariantIds.length === 0
            ? `Match to a product`
            : `Or Select Another Product`}
          <ProductsV2Autocomplete
            selectedProductId={selectedProductIdForVariant}
            onProductSelect={(productId) => {
              // if we are selecting a variant, we want to save the selected product id but not call onProductSelected
              setSelectedProductIdForVariant(productId);
            }}
          />
        </Label>
        {allowProductSelection && (
          <Button
            onClick={() => onProductSelected?.(selectedProductIdForVariant)}
            className="self-end shrink"
          >
            Select Product Only
          </Button>
        )}
      </Grid>

      {/* Product Attribute Selection */}
      {selectedProductIdForVariant && (
        <ProductAttributeSelection
          selectedProductId={selectedProductIdForVariant}
          selectedVariantId={selectedProductVariantId}
          onSelectVariant={handleVariantSelect}
          customerId={customerId}
        />
      )}
    </Grid>
  );
}

export default AssociateProductV2;
